import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'app/moment.override';
import { Moment } from 'moment-timezone';
import { Observable, Subscription } from 'rxjs';
import { DailyChartService } from 'app/services/rszoft/dailychart/daily-chart.service';
import { DiagramPeriodLoaderService } from 'app/services/rszoft/dailychart/diagram-period-loader.service';
import { Dailychartinput } from 'app/services/rszoft/dailychart/models/chart.model';
import { PrintService } from 'app/services/rszoft/print.service';
import { TabOption } from 'app/services/rszoft/report.model';
import type {
    ActivityChartData,
    ChartVisibility,
    TimeRange,
} from 'tacho-activity-chart';
import type { NormalizedDiagram } from '../../../../../components/tacho-activity-chart/diagram-normalizer';
import { toLocalDatesChartData } from '../../../../../components/tacho-activity-chart/diagram-normalizer';
import { buildDailyReportAnnotations, toDailyReportEventRow } from './daily-report-chart-data';

/**
 * The chart view of the daily report.
 *
 * One `tacho-activity-chart` in `stacked` mode shows every day of the current
 * page. The chart draws the activity diagram and, per day, one list-only
 * annotation with the day's unique event names (shift, offences/faults, last
 * download) enumerated on a single line. The report no longer draws a header or
 * an outer box around a chart per day, and the chart never renders projected
 * HTML: it only knows the fixed annotation format.
 *
 * The diagram itself is requested once for the whole report period and cached
 * by {@link DiagramPeriodLoaderService}; every day (and every page) lays the
 * same answer out on its own local day.
 */
@Component({
    selector: 'app-dailychart',
    templateUrl: './dailychart.component.html',
    styleUrls: ['./dailychart.component.css']
})
export class DailychartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    /** The days of a report page (chart view); one chart row per entry. */
    @Input() dailyChartInputs: Dailychartinput[];
    /** The printed day (print view); kept for the one-day-per-chart print flow. */
    @Input() dailyChartInput: Dailychartinput;
    @Input() currentLang: string;
    @Input() indexOfChart: number;
    @Input() responseIndex: number;
    @Input() languageChanged: EventEmitter<string>;
    @Input() isPrinting: boolean;
    @Input() typeOfChart: TabOption;

    @Output() chartLoaded = new EventEmitter<any>();

    timezone = 'CET';

    chartData: ActivityChartData;
    chartVisibility: Partial<ChartVisibility> = {};
    chartLoading = false;

    tabOptions = TabOption;

    private viewReady = false;
    private loadedEmitted = false;
    private dataLoaded = false;
    private chartRequestId = 0;
    private dataSubscription?: Subscription;
    private lastInputs: Dailychartinput[] = [];
    private lastDiagram: NormalizedDiagram | null = null;
    private lastInitializedSource: Dailychartinput[] | Dailychartinput | null = null;

    /** Whether this instance was handed a day to draw. The print view creates placeholders first. */
    get hasChart(): boolean {
        return !this.isEmpty();
    }

    /**
     * The print view titles every chart with its driver/vehicle when the report covers more than one
     * of them. Guarded: the report context (and the print tab) is not always available.
     */
    get isMultiEntityPrint(): boolean {
        const reportContext = this.dailyChartService.getReportContext(this.printService.tab)?.reportContext;
        return (reportContext?.dailyChartResponses?.length ?? 0) > 1;
    }

    constructor(
        private translate: TranslateService,
        public dailyChartService: DailyChartService,
        public printService: PrintService,
        private diagramPeriodLoader: DiagramPeriodLoaderService,
    ) {
    }

    ngOnInit(): void {
        this.initializeChartData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dailyChartInputs || changes.dailyChartInput) {
            this.initializeChartData();
        } else if (changes.currentLang && !this.isEmpty()) {
            // The annotation texts are translated, so the data is rebuilt too.
            if (this.lastDiagram) {
                this.applyChartData(this.lastInputs, this.lastDiagram);
            }
        }
    }

    ngAfterViewInit(): void {
        this.viewReady = true;
        this.emitLoadedWhenReady();
    }

    ngOnDestroy(): void {
        this.dataSubscription?.unsubscribe();
    }

    /* ------------------------------------------------------------------ */
    /* Data loading                                                        */
    /* ------------------------------------------------------------------ */

    private initializeChartData(): void {
        // Angular delivers the initial bindings through both ngOnChanges and
        // ngOnInit; only a new input reference is a new chart.
        const source: Dailychartinput[] | Dailychartinput =
            this.dailyChartInputs?.length ? this.dailyChartInputs : this.dailyChartInput;
        if (source && source === this.lastInitializedSource) { return; }
        this.lastInitializedSource = source ?? null;

        const inputs = this.currentInputs();
        if (!inputs.length) { return; }

        this.timezone = inputs[0].timezone || 'CET';
        this.loadDiagramChart(inputs);
    }

    /**
     * Requests the diagram of the whole report period once (shared by every day
     * and page through the loader) and lays it out on the current page's days.
     */
    private loadDiagramChart(inputs: Dailychartinput[]): void {
        const first = inputs[0];
        const requestId = ++this.chartRequestId;
        this.dataSubscription?.unsubscribe();
        this.dataLoaded = false;
        this.loadedEmitted = false;
        this.chartData = undefined;
        this.chartLoading = true;
        this.lastInputs = inputs;
        this.lastDiagram = null;

        const pagePeriod: TimeRange = {
            start: this.dayStartOf(first).unix(),
            end: this.dayStartOf(inputs[inputs.length - 1]).clone().add(1, 'day').unix() - 1,
        };
        const reportPeriod = this.reportPeriod(pagePeriod);
        const clientId = first.clientId != null
            ? '' + first.clientId
            : localStorage.getItem('selectedCompanyId');

        let request$: Observable<NormalizedDiagram>;
        if (this.typeOfChart === TabOption.DRIVER) {
            if (!first.driverId) {
                this.finishChartLoad(requestId);
                return;
            }
            request$ = this.diagramPeriodLoader.loadDriver(first.driverId, clientId, reportPeriod);
        } else {
            if (!first.vehicleIdentificationNumber) {
                this.finishChartLoad(requestId);
                return;
            }
            request$ = this.diagramPeriodLoader.loadVehicle(
                first.vehicleIdentificationNumber, clientId, reportPeriod);
        }

        this.dataSubscription = request$.subscribe({
            next: (diagram: NormalizedDiagram) => {
                if (requestId !== this.chartRequestId) { return; }
                this.lastDiagram = diagram;
                this.applyChartData(inputs, diagram);
                this.finishChartLoad(requestId);
            },
            error: () => {
                if (requestId === this.chartRequestId) { this.finishChartLoad(requestId); }
            },
        });
    }

    /** Lays the cached period diagram out on the page's days, with annotations. */
    private applyChartData(inputs: Dailychartinput[], diagram: NormalizedDiagram): void {
        const data = toLocalDatesChartData(diagram, this.timezone, inputs.map(input => this.dateKeyOf(input)));
        const annotations = this.buildAnnotations(inputs);
        for (const day of data.days) {
            // The report only ever shows its events as names: one list-only row
            // under the day. Rebuilding the row here keeps them off the lane
            // (no bands, markers or chips), even if a builder starts returning
            // timed entries.
            day.annotations = toDailyReportEventRow(annotations.get(day.date ?? '') ?? []);
        }
        this.chartData = data;
    }

    /** The whole report period, falling back to the page when no report is loaded. */
    private reportPeriod(pagePeriod: TimeRange): TimeRange {
        const context = this.dailyChartService.getReportContext(this.typeOfChart)?.reportContext;
        const start = context?.periodStartTime;
        const end = context?.periodEndTime;
        if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
            return { start, end };
        }
        return pagePeriod;
    }

    /** Per-day annotation lists of the page (chart date -> entries). */
    private buildAnnotations(inputs: Dailychartinput[]): Map<string, ReturnType<typeof buildDailyReportAnnotations>> {
        const annotations = new Map<string, ReturnType<typeof buildDailyReportAnnotations>>();
        const isDriver = this.typeOfChart === TabOption.DRIVER;
        for (const input of inputs) {
            annotations.set(this.dateKeyOf(input), buildDailyReportAnnotations(input, {
                isDriver,
                translate: key => this.translate.instant(key),
                dayLengthSeconds: this.dayLengthSecondsOf(input),
            }));
        }
        return annotations;
    }

    private finishChartLoad(requestId: number): void {
        if (requestId !== this.chartRequestId) { return; }
        this.chartLoading = false;
        this.dataLoaded = true;
        this.emitLoadedWhenReady();
    }

    private emitLoadedWhenReady(): void {
        if (!this.viewReady || this.loadedEmitted || this.isEmpty() || !this.dataLoaded) { return; }
        this.loadedEmitted = true;
        const event = this.isPrinting
            ? this.dailyChartService.getNextEvent(this.indexOfChart, this.responseIndex, this.typeOfChart)
            : { index: this.indexOfChart, responseIndex: this.responseIndex };
        setTimeout(() => {
            if (event) {
                this.chartLoaded.emit(event);
            }
        }, 100);
    }

    /* ------------------------------------------------------------------ */
    /* Day helpers                                                         */
    /* ------------------------------------------------------------------ */

    private currentInputs(): Dailychartinput[] {
        const inputs = this.dailyChartInputs?.length
            ? this.dailyChartInputs
            : (this.dailyChartInput ? [this.dailyChartInput] : []);
        return [...inputs]
            .filter(input => !!input)
            .sort((a, b) => a.dateOfChart - b.dateOfChart);
    }

    private isEmpty(): boolean {
        return this.currentInputs().length === 0;
    }

    private dayStartOf(input: Dailychartinput): Moment {
        return moment(input.dateOfChart).tz(this.timezone).startOf('day');
    }

    private dateKeyOf(input: Dailychartinput): string {
        return this.dayStartOf(input).format('YYYY-MM-DD');
    }

    private dayLengthSecondsOf(input: Dailychartinput): number {
        const start = this.dayStartOf(input);
        return start.clone().add(1, 'day').diff(start, 'seconds');
    }
}
